import React, { useState } from 'react';
import { useWorkflow } from './WorkflowContext.tsx';
import useTheme from '../../hooks/useTheme.ts';
import {
  FiCheck, FiAlertCircle, FiMapPin, FiCalendar, FiLayers,
  FiEdit2, FiChevronDown, FiChevronUp, FiLoader,
  FiDroplet, FiTarget, FiNavigation, FiActivity, FiZap,
} from 'react-icons/fi';

interface PadDeployProps {
  onDeploy?: () => void;
  onEdit?: (step: number) => void;
  isViewer?: boolean;
}

// ── colour state map ───────────────────────────────────────────────
const STATE_COLORS = {
  ready:     { bg: '#8CC54010', border: '#8CC54044', icon: '#8CC540' },
  success:   { bg: '#8CC54018', border: '#8CC540',   icon: '#8CC540' },
  deploying: { bg: '#F9C84410', border: '#F9C84444', icon: '#F9C844' },
  error:     { bg: '#DC354510', border: '#DC354544', icon: '#DC3545' },
} as const;

// ── tiny shared sub-components ─────────────────────────────────────
const IconBadge: React.FC<{ color: string; children: React.ReactNode }> = ({ color, children }) => (
  <span style={{
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    width: 28, height: 28, borderRadius: 8,
    background: color + '1A', color, flexShrink: 0,
  }}>
    {children}
  </span>
);

const EditBtn: React.FC<{ border: string; text2: string; onClick: (e: React.MouseEvent) => void }> = ({ border, text2, onClick }) => (
  <button
    onClick={onClick}
    style={{
      display: 'flex', alignItems: 'center', gap: 4, padding: '3px 9px',
      borderRadius: 20, border: `1px solid ${border}`, background: 'transparent',
      color: text2, fontSize: 11, cursor: 'pointer', marginRight: 8,
    }}
  >
    <FiEdit2 size={10} /> Edit
  </button>
);

const DetailCell: React.FC<{ label: string; value: React.ReactNode; text1: string; text2: string }> = ({ label, value, text1, text2 }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
    <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.6px', color: text2 }}>
      {label}
    </span>
    <span style={{ fontSize: 14, fontWeight: 600, color: text1 }}>{value}</span>
  </div>
);

const Chip: React.FC<{ active: boolean; color?: string; border: string; text2: string; children: React.ReactNode }> = ({ active, color = '#8CC540', border, text2, children }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '3px 8px', borderRadius: 6, fontSize: 11, fontWeight: 500,
    border: `1px solid ${active ? color + '44' : border}`,
    background: active ? color + '18' : 'transparent',
    color: active ? color : text2,
  }}>
    {children}
  </span>
);

const DeployBanner: React.FC<{ state: keyof typeof STATE_COLORS; icon: React.ReactNode; title: string; message: React.ReactNode }> = ({ state, icon, title, message }) => {
  const c = STATE_COLORS[state];
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, padding: '16px 18px', borderRadius: 12, background: c.bg, border: `1px solid ${c.border}` }}>
      <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 38, height: 38, borderRadius: 10, background: c.icon + '22', color: c.icon, flexShrink: 0 }}>
        {icon}
      </span>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>{title}</div>
        <div style={{ fontSize: 13, lineHeight: 1.5, opacity: 0.8 }}>{message}</div>
      </div>
    </div>
  );
};

// ── main component ─────────────────────────────────────────────────
const PadDeploy: React.FC<PadDeployProps> = ({ onDeploy, onEdit, isViewer = false }) => {
  const { theme } = useTheme();
  const { state, validateStep, deployWorkflow, deployProgress } = useWorkflow();
  const { padData, wells, schedule, wellConfigs, uploadedSurveys } = state;
  const isDark = theme === 'dark';

  const [expanded, setExpanded] = useState<string | null>('pad');
  const [confirmed, setConfirmed] = useState(false);

  // tokens
  const surface  = isDark ? '#1E2124' : '#FFFFFF';
  const surface2 = isDark ? '#16181A' : '#F9FAFB';
  const border   = isDark ? '#2D3035' : '#E5E7EB';
  const text1    = isDark ? '#F3F4F6' : '#111827';
  const text2    = isDark ? '#9CA3AF' : '#6B7280';

  const isDeploying = deployProgress.status === 'deploying';
  const isSuccess   = deployProgress.status === 'success';
  const isError     = deployProgress.status === 'error';
  const totalStages = wells.reduce((s, w) => s + w.stages, 0);

  const configuredCount = wells.filter(w => {
    const c = wellConfigs[w.id];
    return c?.casingStrings?.length || c?.perforations?.length || c?.treatment?.length;
  }).length;

  const surveyCount = Object.keys(uploadedSurveys).length;

  const duration = (() => {
    if (!schedule.startDate || !schedule.endDate) return 0;
    return Math.ceil(Math.abs(schedule.endDate.getTime() - schedule.startDate.getTime()) / 86400000);
  })();

  const validations = [
    { id: 'pad',      label: 'Pad Configuration', step: 0, isValid: validateStep(0), msg: padData.company && padData.padName ? `${padData.padName} for ${padData.company}` : 'Pad details incomplete' },
    { id: 'wells',    label: 'Well Configuration', step: 1, isValid: validateStep(1), msg: wells.length > 0 ? `${wells.length} well${wells.length > 1 ? 's' : ''}, ${totalStages} stages` : 'No wells configured' },
    { id: 'schedule', label: 'Schedule',            step: 2, isValid: validateStep(2), msg: schedule.startDate && schedule.endDate ? `${schedule.startDate.toLocaleDateString()} — ${schedule.endDate.toLocaleDateString()}` : 'Schedule not set' },
  ];

  const allValid = validations.every(v => v.isValid);

  const handleDeploy = async () => {
    if (!confirmed || !allValid || isDeploying) return;
    const result = await deployWorkflow();
    if (result.success && onDeploy) setTimeout(onDeploy, 1500);
  };

  const sectionCard: React.CSSProperties = { background: surface, border: `1px solid ${border}`, borderRadius: 12, overflow: 'hidden' };
  const sectionBtn: React.CSSProperties  = { display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '13px 16px', background: 'transparent', border: 'none', cursor: 'pointer', color: text1 };

  const toggle = (s: string) => setExpanded(prev => prev === s ? null : s);

  return (
    <div style={{ padding: '4px 0 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>

      {/* Header */}
      <div>
        <h2 style={{ fontSize: 21, fontWeight: 700, margin: '0 0 4px', color: text1, letterSpacing: '-0.3px' }}>Review & Deploy</h2>
        <p style={{ fontSize: 13, color: text2, margin: 0 }}>Confirm all details before deploying the job</p>
      </div>

      {/* Validation pills */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
        {validations.map(v => (
          <div key={v.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 10, border: `1px solid ${v.isValid ? '#8CC54044' : '#DC354544'}`, background: v.isValid ? '#8CC54010' : '#DC354510' }}>
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 20, height: 20, borderRadius: '50%', background: v.isValid ? '#8CC540' : '#DC3545', color: '#fff', flexShrink: 0 }}>
              {v.isValid ? <FiCheck size={11} /> : <FiAlertCircle size={11} />}
            </span>
            <span style={{ fontWeight: 700, fontSize: 13, color: text1, minWidth: 135 }}>{v.label}</span>
            <span style={{ fontSize: 13, color: text2, flex: 1 }}>{v.msg}</span>
            {!v.isValid && onEdit && (
              <button onClick={() => onEdit(v.step)} style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #DC3545', background: 'transparent', color: '#DC3545', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                Fix →
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Stats bar */}
      <div style={{ display: 'flex', border: `1px solid ${border}`, borderRadius: 12, overflow: 'hidden' }}>
        {[
          { val: wells.length,    lbl: 'Wells',      color: '#3E76FE' },
          { val: totalStages,     lbl: 'Stages',     color: '#8CC540' },
          { val: configuredCount, lbl: 'Configured', color: '#8CC540' },
          { val: surveyCount,     lbl: 'Surveys',    color: '#A78BFA' },
          { val: duration,        lbl: 'Days',       color: '#F9C844' },
        ].map((s, i) => (
          <div key={s.lbl} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, padding: '14px 8px', background: surface, borderLeft: i > 0 ? `1px solid ${border}` : 'none' }}>
            <span style={{ fontSize: 24, fontWeight: 800, color: s.color, letterSpacing: '-1px', lineHeight: 1 }}>{s.val}</span>
            <span style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: text2 }}>{s.lbl}</span>
          </div>
        ))}
      </div>

      {/* Accordion */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>

        {/* Pad Details */}
        <div style={sectionCard}>
          <button style={sectionBtn} onClick={() => toggle('pad')}>
            <IconBadge color="#3E76FE"><FiMapPin size={14} /></IconBadge>
            <span style={{ fontWeight: 700, fontSize: 14, flex: 1, textAlign: 'left' }}>Pad Details</span>
            <span style={{ fontSize: 12, color: text2, marginRight: 8 }}>{padData.padName || 'Unnamed'} · {padData.company || '—'}</span>
            {onEdit && <EditBtn border={border} text2={text2} onClick={e => { e.stopPropagation(); onEdit(0); }} />}
            {expanded === 'pad' ? <FiChevronUp size={15} color={text2} /> : <FiChevronDown size={15} color={text2} />}
          </button>
          {expanded === 'pad' && (
            <div style={{ padding: 16, borderTop: `1px solid ${border}`, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 16 }}>
              <DetailCell label="Company"  value={padData.company || '—'}  text1={text1} text2={text2} />
              <DetailCell label="Pad Name" value={padData.padName || '—'}  text1={text1} text2={text2} />
              <DetailCell label="Location" value={padData.county && padData.state ? `${padData.county}, ${padData.state}` : padData.state || '—'} text1={text1} text2={text2} />
              <DetailCell label="Fleet"    value={padData.fleet ? `Fleet ${padData.fleet}` : '—'} text1={text1} text2={text2} />
            </div>
          )}
        </div>

        {/* Wells */}
        <div style={sectionCard}>
          <button style={sectionBtn} onClick={() => toggle('wells')}>
            <IconBadge color="#8CC540"><FiLayers size={14} /></IconBadge>
            <span style={{ fontWeight: 700, fontSize: 14, flex: 1, textAlign: 'left' }}>Wells ({wells.length})</span>
            <span style={{ fontSize: 12, color: text2, marginRight: 8 }}>{totalStages} stages total</span>
            {onEdit && <EditBtn border={border} text2={text2} onClick={e => { e.stopPropagation(); onEdit(1); }} />}
            {expanded === 'wells' ? <FiChevronUp size={15} color={text2} /> : <FiChevronDown size={15} color={text2} />}
          </button>
          {expanded === 'wells' && (
            <div style={{ padding: 16, borderTop: `1px solid ${border}`, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {wells.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '20px 0', color: text2, fontSize: 13 }}>No wells configured</div>
              ) : wells.map(well => {
                const config = wellConfigs[well.id];
                const survey = uploadedSurveys[well.id];
                const configured = !!(config?.casingStrings?.length || config?.perforations?.length || config?.treatment?.length);
                return (
                  <div key={well.id} style={{ border: `1px solid ${border}`, borderRadius: 10, padding: '14px 16px', background: surface2, display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontWeight: 700, fontSize: 14, color: text1 }}>{well.name}</span>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: configured ? '#8CC540' : '#4B5563', boxShadow: configured ? '0 0 6px #8CC54088' : 'none' }} />
                    </div>

                    <div style={{ display: 'flex', border: `1px solid ${border}`, borderRadius: 8, overflow: 'hidden' }}>
                      {[
                        { val: well.stages, lbl: 'Stages' },
                        { val: well.tvd ? well.tvd.toLocaleString() + ' ft' : '—', lbl: 'TVD' },
                        { val: well.td  ? well.td.toLocaleString()  + ' ft' : '—', lbl: 'TD'  },
                      ].map((m, i) => (
                        <div key={m.lbl} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '8px 4px', borderLeft: i > 0 ? `1px solid ${border}` : 'none' }}>
                          <span style={{ fontSize: 17, fontWeight: 800, color: '#8CC540', letterSpacing: '-0.5px' }}>{m.val}</span>
                          <span style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px', color: text2 }}>{m.lbl}</span>
                        </div>
                      ))}
                    </div>

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      <Chip active={!!(config?.casingStrings?.length)} border={border} text2={text2}>
                        <FiDroplet size={10} /> {config?.casingStrings?.length ? `${config.casingStrings.length} casing` : 'No casing'}
                      </Chip>
                      <Chip active={!!(config?.perforations?.length)} border={border} text2={text2}>
                        <FiTarget size={10} /> {config?.perforations?.length ? `${new Set(config.perforations.map(p => p.stage)).size} perf stages` : 'No perfs'}
                      </Chip>
                      <Chip active={!!(config?.treatment?.length)} border={border} text2={text2}>
                        <FiActivity size={10} /> {config?.treatment?.length ? `${config.treatment.length} steps` : 'No treatment'}
                      </Chip>
                      <Chip active={!!survey} color="#A78BFA" border={border} text2={text2}>
                        <FiNavigation size={10} /> {survey ? `${survey.data.surveys.length} survey pts` : 'No survey'}
                      </Chip>
                    </div>

                    {well.api && <span style={{ fontSize: 11, color: text2, fontFamily: 'monospace' }}>API: {well.api}</span>}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Schedule */}
        <div style={sectionCard}>
          <button style={sectionBtn} onClick={() => toggle('schedule')}>
            <IconBadge color="#F9C844"><FiCalendar size={14} /></IconBadge>
            <span style={{ fontWeight: 700, fontSize: 14, flex: 1, textAlign: 'left' }}>Schedule</span>
            <span style={{ fontSize: 12, color: text2, marginRight: 8 }}>
              {schedule.startDate && schedule.endDate
                ? `${schedule.startDate.toLocaleDateString()} — ${schedule.endDate.toLocaleDateString()}`
                : 'Not scheduled'}
            </span>
            {onEdit && <EditBtn border={border} text2={text2} onClick={e => { e.stopPropagation(); onEdit(2); }} />}
            {expanded === 'schedule' ? <FiChevronUp size={15} color={text2} /> : <FiChevronDown size={15} color={text2} />}
          </button>
          {expanded === 'schedule' && (
            <div style={{ padding: 16, borderTop: `1px solid ${border}` }}>
              {schedule.startDate && schedule.endDate && (
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 20 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#8CC540' }} />
                    <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: text2 }}>Start</span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: text1, whiteSpace: 'nowrap' }}>
                      {schedule.startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                  </div>
                  <div style={{ flex: 1, height: 2, margin: '0 12px', marginTop: -28, background: 'linear-gradient(90deg, #8CC540, #F9C844)', position: 'relative' }}>
                    <span style={{ position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)', fontSize: 11, fontWeight: 700, color: '#8CC540', background: surface, padding: '0 6px', whiteSpace: 'nowrap' }}>
                      {duration} days
                    </span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#F9C844' }} />
                    <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: text2 }}>End</span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: text1, whiteSpace: 'nowrap' }}>
                      {schedule.endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                  </div>
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 16 }}>
                <DetailCell label="Duration"     value={`${duration} days`}                         text1={text1} text2={text2} />
                <DetailCell label="Stages / Day" value={schedule.expectedStagesPerDay || '—'}        text1={text1} text2={text2} />
                {schedule.district && <DetailCell label="District" value={schedule.district}         text1={text1} text2={text2} />}
                {schedule.operator && <DetailCell label="Operator" value={schedule.operator}         text1={text1} text2={text2} />}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Deploy zone */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {isSuccess ? (
          <DeployBanner state="success" icon={<FiCheck size={20} />} title="Deploy Successful"
            message={`${padData.padName} is live with ${wells.length} wells and ${totalStages} stages. Redirecting…`} />

        ) : isDeploying ? (
          <DeployBanner state="deploying" icon={<span style={{ animation: 'spin 1s linear infinite', display: 'flex' }}><FiLoader size={20} /></span>}
            title="Deploying…"
            message={
              <div>
                <div>{deployProgress.currentStep}</div>
                {deployProgress.completedSteps.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                    {deployProgress.completedSteps.map((s, i) => (
                      <span key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#8CC540', fontWeight: 500 }}>
                        <FiCheck size={10} /> {s}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            }
          />

        ) : isError ? (
          <>
            <DeployBanner state="error" icon={<FiAlertCircle size={20} />} title="Deploy Failed"
              message={deployProgress.error || 'An unexpected error occurred.'} />
            <DeployButton bg="#F59E0B" onClick={handleDeploy}>Retry Deploy</DeployButton>
          </>

        ) : allValid ? (
          <>
            {isViewer && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 8, background: '#F9C84418', border: '1px solid #F9C84450', color: '#F9C844', fontSize: 13 }}>
                <span>👁</span> View only — contact a Manager or Editor to deploy this job
              </div>
            )}
            <DeployBanner state="ready" icon={<FiZap size={20} />} title="Ready to Deploy"
              message='All configurations validated. Click "Deploy Job" to save all data and activate this job.' />

            <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderRadius: 10, border: `1px solid ${border}`, background: surface, cursor: isViewer ? 'default' : 'pointer', fontSize: 13, color: text1 }}>
              <input
                type="checkbox"
                checked={confirmed}
                onChange={e => !isViewer && setConfirmed(e.target.checked)}
                disabled={isViewer}
                style={{ width: 16, height: 16, accentColor: '#8CC540', flexShrink: 0 }}
              />
              I confirm that all job details are correct and ready for deployment
            </label>

            <DeployButton
              bg={confirmed && !isViewer ? '#8CC540' : isDark ? '#2A2D30' : '#E5E7EB'}
              disabled={!confirmed || isViewer}
              onClick={handleDeploy}
            >
              <FiZap size={15} /> Deploy Job
            </DeployButton>
          </>

        ) : (
          <DeployBanner state="error" icon={<FiAlertCircle size={20} />} title="Configuration Incomplete"
            message="Complete all required steps before deploying. Use the Fix buttons above to navigate to incomplete sections." />
        )}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

const DeployButton: React.FC<{ bg: string; disabled?: boolean; onClick: () => void; children: React.ReactNode }> = ({ bg, disabled, onClick, children }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    style={{
      width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
      gap: 8, padding: '14px 24px', borderRadius: 12, border: 'none',
      fontSize: 15, fontWeight: 700, letterSpacing: '0.2px',
      cursor: disabled ? 'not-allowed' : 'pointer',
      background: bg,
      color: disabled ? '#9CA3AF' : '#FFFFFF',
      transition: 'all 0.2s',
    }}
  >
    {children}
  </button>
);

export default PadDeploy;